import React, { useState, useEffect } from 'react';
import { useCategories, useSubcategories } from '../../hooks/useCategories';
import { useProjects } from '../../hooks/useProjects';
import { useCreateExpense, useUpdateExpense } from '../../hooks/useExpenses';
import { useAuth } from '../../contexts/AuthContext';
import { assetsApi } from '../../services/api/assets';
import type { Expense, ExpenseCreate } from '../../types';
import type { AssetSnapshot } from '../../types/asset';
import AttachmentUpload from './AttachmentUpload';
import AssetFieldSelector from './AssetFieldSelector';

interface ExpenseFormProps {
  expense?: Expense;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onSuccess, onCancel }) => {
  const isEditing = !!expense;
  const { user } = useAuth();
  const companyId = user?.companyId || '';
  
  // Form state
  const [projectId, setProjectId] = useState(expense?.projectId || '');
  const [date, setDate] = useState(expense?.date || new Date().toISOString().split('T')[0]);
  const [provider, setProvider] = useState(expense?.provider || '');
  const [categoryId, setCategoryId] = useState(expense?.categoryId || '');
  const [subcategoryId, setSubcategoryId] = useState(expense?.subcategoryId || '');
  const [amount, setAmount] = useState(expense?.amount?.toString() || '');
  const [description, setDescription] = useState(expense?.description || '');
  const [documentNumber, setDocumentNumber] = useState(expense?.documentNumber || '');
  const [attachmentIds, setAttachmentIds] = useState<string[]>(expense?.attachmentIds || []);
  const [createdExpenseId, setCreatedExpenseId] = useState<string | undefined>(expense?.expenseId);
  const [assetSelections, setAssetSelections] = useState<Record<string, string>>({});
  
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Queries
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: subcategories, isLoading: subcategoriesLoading } = useSubcategories(categoryId);
  
  // Get selected category to check for custom fields
  const selectedCategory = categories?.find(cat => cat.categoryId === categoryId);
  const customFields = selectedCategory?.customFields || [];
  
  // Mutations
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  
  // Reset subcategory and asset selections when category changes
  useEffect(() => {
    if (!isEditing) {
      setSubcategoryId('');
      setAssetSelections({});
    }
  }, [categoryId, isEditing]);
  
  // Initialize asset selections when editing
  useEffect(() => {
    if (isEditing && expense?.assets && customFields.length > 0) {
      const selections: Record<string, string> = {};
      expense.assets.forEach(asset => {
        // Find the custom field that matches this asset's type
        const matchingField = customFields.find(cf => cf.assetTypeId === asset.assetTypeId);
        if (matchingField) {
          selections[matchingField.fieldId] = asset.assetId;
        }
      });
      setAssetSelections(selections);
    }
  }, [isEditing, expense?.assets, customFields]);
  
  // Real-time validation
  const validateField = (field: string, value: string): string => {
    // Check if it's an asset field
    if (field.startsWith('asset-')) {
      const fieldId = field.replace('asset-', '');
      const customField = customFields.find(cf => cf.fieldId === fieldId);
      if (customField?.required && !value) {
        return `${customField.fieldName} es requerido`;
      }
      return '';
    }
    
    switch (field) {
      case 'projectId':
        return value ? '' : 'Proyecto es requerido';
      case 'date':
        return value ? '' : 'Fecha es requerida';
      case 'provider':
        return value.trim() ? '' : 'Proveedor es requerido';
      case 'categoryId':
        return value ? '' : 'Categoría es requerida';
      case 'subcategoryId':
        return value ? '' : 'Subcategoría es requerida';
      case 'amount':
        if (!value) return 'Monto es requerido';
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue <= 0) return 'Monto debe ser mayor que 0';
        if (!/^\d+(\.\d{1,2})?$/.test(value)) return 'Monto debe tener máximo 2 decimales';
        return '';
      case 'description':
        return value.trim() ? '' : 'Descripción es requerida';
      default:
        return '';
    }
  };
  
  const handleFieldChange = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {
      projectId: validateField('projectId', projectId),
      date: validateField('date', date),
      provider: validateField('provider', provider),
      categoryId: validateField('categoryId', categoryId),
      subcategoryId: validateField('subcategoryId', subcategoryId),
      amount: validateField('amount', amount),
      description: validateField('description', description),
    };
    
    // Validate asset fields
    customFields.forEach(field => {
      const assetFieldKey = `asset-${field.fieldId}`;
      newErrors[assetFieldKey] = validateField(assetFieldKey, assetSelections[field.fieldId] || '');
    });
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Build asset snapshots from selections
    const assetSnapshots: AssetSnapshot[] = [];
    
    for (const field of customFields) {
      const assetId = assetSelections[field.fieldId];
      if (assetId && field.assetTypeId) {
        try {
          const asset = await assetsApi.getById(assetId, companyId);
          assetSnapshots.push({
            assetId: asset.assetId,
            assetTypeId: field.assetTypeId,
            displayName: asset.displayName,
            fieldValues: asset.fieldValues,
          });
        } catch (error) {
          console.error('Error fetching asset data:', error);
        }
      }
    }
    
    const expenseData: ExpenseCreate = {
      projectId,
      date,
      provider: provider.trim(),
      categoryId,
      subcategoryId,
      amount: parseFloat(amount),
      description: description.trim(),
      documentNumber: documentNumber.trim() || undefined,
      assets: assetSnapshots.length > 0 ? assetSnapshots : undefined,
    };
    
    try {
      if (isEditing) {
        // For editing, include attachmentIds if any
        if (attachmentIds.length > 0) {
          expenseData.attachmentIds = attachmentIds;
        }
        await updateExpense.mutateAsync({
          expenseId: expense.expenseId,
          data: expenseData,
        });
        onSuccess?.();
      } else {
        console.log('[ExpenseForm] Creating expense without attachments...');
        // For creation: create expense first WITHOUT attachments
        const createdExpense = await createExpense.mutateAsync(expenseData);
        console.log('[ExpenseForm] Expense created:', createdExpense.expenseId);
        
        // Set the created expense ID so AttachmentUpload can upload files
        console.log('[ExpenseForm] Setting createdExpenseId to:', createdExpense.expenseId);
        setCreatedExpenseId(createdExpense.expenseId);
        
        // AttachmentUpload will handle uploads and call handleUploadComplete when done
        // Don't call onSuccess() here - wait for uploads to complete
      }
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };
  
  const handleUploadComplete = async (uploadedAttachmentIds: string[]) => {
    console.log('[ExpenseForm] handleUploadComplete called with uploadedAttachmentIds:', uploadedAttachmentIds);
    
    // If we have attachments, update the expense
    if (createdExpenseId && !isEditing && uploadedAttachmentIds.length > 0) {
      console.log('[ExpenseForm] Updating expense with attachment IDs:', uploadedAttachmentIds);
      try {
        await updateExpense.mutateAsync({
          expenseId: createdExpenseId,
          data: {
            attachmentIds: uploadedAttachmentIds,
          },
        });
        console.log('[ExpenseForm] Expense updated with attachments');
      } catch (error) {
        console.error('Error updating expense with attachments:', error);
      }
    }
    
    // Always call onSuccess when uploads are complete (whether there were attachments or not)
    console.log('[ExpenseForm] Calling onSuccess');
    onSuccess?.();
  };
  
  const handleAttachmentsChange = async (newAttachmentIds: string[]) => {
    console.log('[ExpenseForm] handleAttachmentsChange called with:', newAttachmentIds);
    setAttachmentIds(newAttachmentIds);
  };
  
  const isLoading = projectsLoading || categoriesLoading;
  const isSaving = createExpense.isLoading || updateExpense.isLoading;
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
      {/* Project - full width */}
      <div>
        <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
          Proyecto <span className="text-red-500">*</span>
        </label>
        <select
          id="projectId"
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value);
            handleFieldChange('projectId', e.target.value);
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.projectId ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isEditing}
        >
          <option value="">Seleccionar proyecto</option>
          {projects?.map((project) => (
            <option key={project.projectId} value={project.projectId}>
              {project.name}{project.description ? ` — ${project.description}` : ''}{project.orderNumber ? ` (OC: ${project.orderNumber})` : ''}
            </option>
          ))}
        </select>
        {errors.projectId && (
          <p className="mt-1 text-sm text-red-600">{errors.projectId}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Document Number */}
        <div>
          <label htmlFor="documentNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Nº Documento
          </label>
          <input
            type="text"
            id="documentNumber"
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="Ej: 392658"
            maxLength={50}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
            Fecha <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              handleFieldChange('date', e.target.value);
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.date ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.date && (
            <p className="mt-1 text-sm text-red-600">{errors.date}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Provider */}
        <div>
          <label htmlFor="provider" className="block text-sm font-medium text-gray-700 mb-1">
            Proveedor <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="provider"
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              handleFieldChange('provider', e.target.value);
            }}
            placeholder="Nombre del proveedor"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.provider ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.provider && (
            <p className="mt-1 text-sm text-red-600">{errors.provider}</p>
          )}
        </div>
        
        {/* Amount */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Monto <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              handleFieldChange('amount', e.target.value);
            }}
            placeholder="0.00"
            step="0.01"
            min="0"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.amount ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.amount && (
            <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Category */}
        <div>
          <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
            Categoría <span className="text-red-500">*</span>
          </label>
          <select
            id="categoryId"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              handleFieldChange('categoryId', e.target.value);
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.categoryId ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Seleccionar categoría</option>
            {categories?.filter(cat => cat.isActive).map((category) => (
              <option key={category.categoryId} value={category.categoryId}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.categoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
          )}
        </div>
        
        {/* Subcategory */}
        <div>
          <label htmlFor="subcategoryId" className="block text-sm font-medium text-gray-700 mb-1">
            Subcategoría <span className="text-red-500">*</span>
          </label>
          <select
            id="subcategoryId"
            value={subcategoryId}
            onChange={(e) => {
              setSubcategoryId(e.target.value);
              handleFieldChange('subcategoryId', e.target.value);
            }}
            disabled={!categoryId || subcategoriesLoading}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.subcategoryId ? 'border-red-500' : 'border-gray-300'
            } ${!categoryId ? 'bg-gray-100' : ''}`}
          >
            <option value="">
              {!categoryId ? 'Primero seleccione categoría' : 'Seleccionar subcategoría'}
            </option>
            {subcategories?.filter(sub => sub.isActive).map((subcategory) => (
              <option key={subcategory.subcategoryId} value={subcategory.subcategoryId}>
                {subcategory.name}
              </option>
            ))}
          </select>
          {errors.subcategoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.subcategoryId}</p>
          )}
        </div>
      </div>
      
      {/* Dynamic Asset Fields */}
      {customFields.length > 0 && categoryId && (
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Activos Asociados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customFields.map((field) => (
              <AssetFieldSelector
                key={field.fieldId}
                field={field}
                value={assetSelections[field.fieldId] || ''}
                onChange={(value) => {
                  setAssetSelections(prev => ({
                    ...prev,
                    [field.fieldId]: value
                  }));
                  if (field.required) {
                    handleFieldChange(`asset-${field.fieldId}`, value);
                  }
                }}
                error={errors[`asset-${field.fieldId}`]}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Descripción <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            handleFieldChange('description', e.target.value);
          }}
          placeholder="Descripción del gasto"
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>
      
      {/* Attachments */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Adjuntos
        </label>
        <AttachmentUpload
          expenseId={createdExpenseId}
          attachmentIds={attachmentIds}
          onChange={handleAttachmentsChange}
          onUploadComplete={handleUploadComplete}
          isEditing={isEditing}
        />
      </div>
      
      {/* Error message */}
      {(createExpense.isError || updateExpense.isError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {(createExpense.error as Error)?.message || (updateExpense.error as Error)?.message || 'Error al guardar el gasto'}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Guardando...
            </>
          ) : (
            isEditing ? 'Actualizar Gasto' : 'Crear Gasto'
          )}
        </button>
      </div>
    </form>
  );
};

export default ExpenseForm;
